import { useNavigate } from 'react-router';
import React, { useRef, useEffect } from 'react';

import { Box } from '@mui/material';

import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import useNotifications from '../../hooks/useNotifications';
import documentService from '../../services/documentService';

//----------------------------------------------------------------------

type EideasyWidget = HTMLElement & {
  countryCode?: string;
  language?: string;
  sandbox?: boolean;
  clientId?: string;
  docId?: string;
  enabledMethods?: { signature: string[] };
  selectedMethod?: string | null;
  enabledCountries?: string[];
  inputValues?: { idcode?: string; phone?: string };
  onSuccess?: (data: unknown) => void;
  onFail?: (err: unknown) => void;
  uiTheme?: {
    colorPrimary?: string;
    colorSecondary?: string;
    textColor?: string;
    buttonTextColor?: string;
    buttonColor?: string;
    buttonColorHover?: string;
    inputLabelColor?: string;
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: string | number;
  };
};

type EideasyProps = {
  documentId: number;
  eidEasyDocumentId: string;
  clientId: string;
  enabledCountries: string[];
  enabledMethods: string[];
  setIsLoading: (value: boolean) => void;
  setIsOpen: (value: boolean) => void;
};

const Eideasy: React.FC<EideasyProps> = ({
  documentId,
  eidEasyDocumentId,
  clientId,
  enabledCountries,
  enabledMethods,
  setIsLoading,
  setIsOpen,
}) => {
  const { currentLang } = useLocales();
  const { document } = useSelector((state) => state.document);
  const navigate = useNavigate();
  const { enqueueSuccess, enqueueError } = useNotifications();
  const holderRef = useRef<HTMLDivElement>(null);

  const language =
    currentLang.value.toLowerCase() === 'ee' ? 'et' : currentLang.value.toLowerCase();

  const getSandboxMode = (): boolean => {
    const { hostname } = window.location;

    // Check if running on localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return true;
    }

    // Check if subdomain is "dev"
    const subdomain = hostname.split('.')[1];
    return subdomain === 'dev';
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (typeof window === 'undefined') return;

      if (!customElements.get('eideasy-widget')) {
        await import('@eid-easy/eideasy-widget');
      }

      if (!mounted) return;

      const holder = holderRef.current;

      if (!holder) return;

      const widget = globalThis.document.createElement('eideasy-widget') as EideasyWidget;

      const settings: Partial<EideasyWidget> = {
        language,
        sandbox: getSandboxMode(),
        clientId,
        docId: eidEasyDocumentId,
        enabledMethods: { signature: enabledMethods },
        selectedMethod: null,
        enabledCountries,
        async onSuccess(data) {
          try {
            setIsLoading(true);

            await documentService.completeEidEasySigning(documentId, eidEasyDocumentId);

            enqueueSuccess('documents.snackbar.signedSuccessfully');

            if (document) {
              await documentService.validateDocumentById([document.id]);
            }
          } catch (e) {
            enqueueError(e);
          } finally {
            navigate(0);
          }
        },
        onFail(error) {
          enqueueError(error);
        },
        uiTheme: {
          colorPrimary: '#d51111',
          colorSecondary: '#B63F17',
          textColor: '#303A46',
          buttonTextColor: '#fff',
          buttonColor: '#E8683E',
          buttonColorHover: '#B63F17',
          inputLabelColor: '#303A46',
          fontFamily: 'inherit',
          fontSize: '16px',
          fontWeight: 'bold',
        },
      };

      Object.assign(widget, settings);
      holder.replaceChildren(widget);
    })();

    return () => {
      mounted = false;
      const holder = holderRef.current;
      if (holder) holder.replaceChildren();
    };
  }, []);

  return <Box ref={holderRef} sx={{ width: '100%' }} />;
};

export default Eideasy;
